import { useEffect, useState } from "react";
import { X, ImageOff, Maximize2 } from "lucide-react";
import { getWorkerApplicationPassportDocumentBlob } from "../workerApplications/workerApplicationsApi.js";

import { MediaSkeleton } from "../components/Skeleton.jsx";
/**
 * P157: the applicant drawer used to show only the passport document's stored
 * FILE NAME ("passport_front.jpg") - which reads to an operator as a path, not
 * a document, and is useless when the backoffice needs to actually verify the
 * person. This shows the REAL image.
 *
 * It fetches the actual saved bytes through the same authenticated
 * GET .../passport-document/file endpoint (P141R16) the War Room passport modal
 * already uses - keyed by applicationId only, never a storage key or path - and
 * renders a real inline thumbnail that enlarges to a full modal on click. A 404
 * is an honest "no document uploaded" empty state, never a placeholder image.
 *
 * The object URL is created once per applicationId and revoked on cleanup, so
 * an operator paging through applicants does not leak a blob URL per open.
 */
export default function PassportDocumentViewer({ applicationId, workerName, hasDocument }) {
  const [state, setState] = useState({ status: "idle", objectUrl: null, error: null });
  const [enlarged, setEnlarged] = useState(false);

  useEffect(() => {
    if (!applicationId || !hasDocument) {
      setState({ status: "empty", objectUrl: null, error: null });
      return undefined;
    }
    let cancelled = false;
    let createdUrl = null;
    setState({ status: "loading", objectUrl: null, error: null });
    getWorkerApplicationPassportDocumentBlob(applicationId)
      .then(({ blob }) => {
        if (cancelled) {
          return;
        }
        createdUrl = URL.createObjectURL(blob);
        setState({ status: "ready", objectUrl: createdUrl, error: null });
      })
      .catch((error) => {
        if (!cancelled) {
          setState({ status: "error", objectUrl: null, error });
        }
      });
    return () => {
      cancelled = true;
      if (createdUrl) {
        URL.revokeObjectURL(createdUrl);
      }
    };
  }, [applicationId, hasDocument]);

  useEffect(() => {
    if (!enlarged) {
      return undefined;
    }
    function handleKeyDown(event) {
      if (event.key !== "Escape") {
        return;
      }
      // P157-R8: capture phase + stopImmediatePropagation, so Escape closes ONLY
      // the topmost layer. This viewer is now opened from inside the detailed
      // profile dialog, which has its own document-level Escape handler; with
      // both listening, one press dismissed the enlarged document AND the dialog
      // behind it, throwing away the operator's place in a 24-field form.
      event.stopImmediatePropagation();
      setEnlarged(false);
    }
    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [enlarged]);

  if (state.status === "empty") {
    return (
      <div className="wx-passport-doc wx-passport-doc--empty">
        <ImageOff size={18} aria-hidden="true" />
        <span>ยังไม่มีรูปเอกสารในระบบ</span>
      </div>
    );
  }

  if (state.status === "loading" || state.status === "idle") {
    return (
      <div className="wx-passport-doc wx-passport-doc--loading">
        <MediaSkeleton label="กำลังโหลดรูปเอกสาร..." height={220} />
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="wx-passport-doc wx-passport-doc--empty">
        <ImageOff size={18} aria-hidden="true" />
        <span>
          {state.error?.status === 404 || state.error?.status === 403
            ? "ยังไม่มีรูปเอกสารในระบบ"
            : "โหลดรูปเอกสารไม่สำเร็จ กรุณาลองใหม่อีกครั้ง"}
        </span>
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        className="wx-passport-doc wx-passport-doc--thumb"
        onClick={() => setEnlarged(true)}
        aria-label={`ดูรูปเอกสารของ ${workerName ?? "ผู้สมัคร"} แบบเต็ม`}
      >
        <img src={state.objectUrl} alt={`เอกสารประจำตัวของ ${workerName ?? "ผู้สมัคร"}`} />
        <span className="wx-passport-doc-zoom">
          <Maximize2 size={14} aria-hidden="true" />
          ดูเต็ม
        </span>
      </button>

      {enlarged && (
        <div className="wx-passport-modal-backdrop" onClick={() => setEnlarged(false)}>
          <div
            className="wx-passport-modal"
            role="dialog"
            aria-modal="true"
            aria-label={`รูปเอกสารของ ${workerName ?? "ผู้สมัคร"}`}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="wx-passport-modal-head">
              <strong>{workerName ?? "-"}</strong>
              <button type="button" className="wx-passport-modal-close" onClick={() => setEnlarged(false)} aria-label="ปิด">
                <X size={18} aria-hidden="true" />
              </button>
            </div>
            <div className="wx-passport-modal-body">
              <img src={state.objectUrl} alt={`เอกสารประจำตัวของ ${workerName ?? "ผู้สมัคร"}`} className="wx-passport-modal-image" />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
